// SuperAdminDashboard — Premium analytics SaaS design
import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { motion } from "framer-motion";
import axios from "axios";
import { API } from "../App";
import {
  Store, Wallet, Users, AlertTriangle, RefreshCw,
  TrendingUp, TrendingDown, ArrowRight, Clock,
  DollarSign, WifiOff, ShieldX, CreditCard, Percent
} from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis,
  CartesianGrid, Tooltip
} from "recharts";
import { COLORS } from "../components/SuperAdminLayout";

// Plan config
const PLANS = {
  starter: { name: "Starter", price: 29, color: "#F59E0B", dot: "#F59E0B" },
  pro: { name: "Pro", price: 49, color: "#8B5CF6", dot: "#8B5CF6" },
  business: { name: "Business", price: 89, color: "#3B82F6", dot: "#3B82F6" },
};

const PRICING = { oneshot: 485, maintenance: 30, extraUser: 30 };

// Compute history from boutique data + payments
const computeHistory = (boutiques, payments) => {
  const months = [];
  const now = new Date();
  for (let i = 5; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push({ date: d, month: format(d, "MMM", { locale: fr }), newBoutiques: 0, mrr: 0 });
  }
  boutiques.forEach(b => {
    const created = new Date(b.createdAt);
    months.forEach(m => {
      if (created.getMonth() === m.date.getMonth() && created.getFullYear() === m.date.getFullYear()) m.newBoutiques++;
    });
  });
  let cumRevenue = 0;
  const sorted = [...payments].sort((a, b) => new Date(a.date || a.created_at) - new Date(b.date || b.created_at));
  months.forEach(m => {
    sorted.forEach(p => {
      const pd = new Date(p.date || p.created_at);
      if (pd.getMonth() === m.date.getMonth() && pd.getFullYear() === m.date.getFullYear()) cumRevenue += p.montant || 0;
    });
    m.mrr = Math.round(cumRevenue);
  });
  return months;
};

const getInitials = (name) => {
  if (!name) return "B";
  const w = name.trim().split(/\s+/);
  return w.length === 1 ? w[0].substring(0, 2).toUpperCase() : (w[0][0] + w[1][0]).toUpperCase();
};

// Avatar colors by index
const AVATAR_COLORS = ["#FF4500", "#8B5CF6", "#3B82F6", "#F59E0B", "#10B981", "#EC4899"];

const truncateNames = (items, max = 3) => {
  const names = items.map(b => b.name);
  if (names.length <= max) return names.join(", ");
  return names.slice(0, max).join(", ") + ` et ${names.length - max} autre${names.length - max > 1 ? "s" : ""}`;
};

export default function SuperAdminDashboard() {
  const navigate = useNavigate();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [chartView, setChartView] = useState("mrr");
  const [data, setData] = useState({ boutiques: [], mrrHistory: [] });

  const loadData = async () => {
    setIsRefreshing(true);
    try {
      const [boutiquesRes, paymentsRes] = await Promise.all([
        axios.get(`${API}/admin/boutiques`),
        axios.get(`${API}/superadmin/payments`).catch(() => ({ data: [] })),
      ]);
      const boutiques = boutiquesRes.data.map(b => ({
        id: b.id || b.admin_id,
        name: b.boutique_name || b.slug,
        slug: b.slug,
        plan: b.plan || "starter",
        status: b.status || "active",
        users: b.users || 1,
        compteValide: b.compteValide === true,
        oneshotPaid: false,
        maintenancePaid: false,
        createdAt: b.created_at || new Date().toISOString(),
        lastLogin: b.last_login || b.created_at || new Date().toISOString(),
      }));
      const payments = paymentsRes.data || [];
      payments.forEach(p => {
        const match = boutiques.find(b => b.slug === p.boutique_slug || b.name === p.boutique_name);
        if (match) {
          if (p.type === "one-shot" && p.statut === "payé") match.oneshotPaid = true;
          if (p.type === "maintenance" && p.statut === "payé") match.maintenancePaid = true;
        }
      });
      setData({ boutiques, mrrHistory: computeHistory(boutiques, payments) });
    } catch (err) { console.error("Erreur:", err); }
    finally { setIsRefreshing(false); setLoading(false); }
  };

  useEffect(() => { loadData(); }, []);

  // KPI computations
  const kpis = useMemo(() => {
    const { boutiques } = data;
    const activeBoutiques = boutiques.filter(b => b.status === "active");
    const now = new Date();
    const newThisMonth = boutiques.filter(b => {
      const c = new Date(b.createdAt);
      return c.getMonth() === now.getMonth() && c.getFullYear() === now.getFullYear();
    }).length;
    const maintenanceActive = boutiques.filter(b => b.status === "active" && b.maintenancePaid);
    const mrrBase = maintenanceActive.length * PRICING.maintenance;
    const mrrExtra = maintenanceActive.reduce((s, b) => s + Math.max(0, b.users - 1) * PRICING.extraUser, 0);
    const totalUsers = boutiques.reduce((sum, b) => sum + b.users, 0);
    const thirtyDaysAgo = new Date(Date.now() - 30 * 86400000);
    const inactiveBoutiques = boutiques.filter(b => new Date(b.lastLogin) < thirtyDaysAgo);
    return { activeBoutiques: activeBoutiques.length, newThisMonth, mrr: mrrBase + mrrExtra, totalUsers, totalBoutiques: boutiques.length, inactiveBoutiques: inactiveBoutiques.length };
  }, [data]);

  // Alerts
  const alerts = useMemo(() => {
    const { boutiques } = data;
    const now = new Date();
    const sevenDaysAgo = new Date(now - 7 * 86400000);
    const thirtyDaysAgo = new Date(now - 30 * 86400000);
    return {
      oneshotOverdue: boutiques.filter(b => !b.oneshotPaid && new Date(b.createdAt) < sevenDaysAgo),
      maintenanceOverdue: boutiques.filter(b => b.status === "active" && !b.maintenancePaid),
      inactive30: boutiques.filter(b => new Date(b.lastLogin) < thirtyDaysAgo),
      pendingValidation: boutiques.filter(b => !b.compteValide),
    };
  }, [data]);

  // Plan distribution
  const planDistribution = useMemo(() => {
    const dist = { starter: 0, pro: 0, business: 0 };
    data.boutiques.forEach(b => { if (dist[b.plan] !== undefined) dist[b.plan]++; });
    const total = data.boutiques.length || 1;
    return Object.entries(dist).map(([key, value]) => ({
      key, name: PLANS[key].name, value, pct: Math.round((value / total) * 100), color: PLANS[key].color,
    }));
  }, [data]);

  // Recent signups
  const lastRegistrations = useMemo(() => {
    return [...data.boutiques].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).slice(0, 5);
  }, [data]);

  // Payment stats
  const paymentStats = useMemo(() => {
    const paid = data.boutiques.filter(b => b.oneshotPaid).length;
    const total = data.boutiques.length || 1;
    return { paidCount: paid, conversionRate: Math.round((paid / total) * 100) };
  }, [data]);

  const todayFormatted = format(new Date(), "EEEE d MMMM yyyy", { locale: fr });

  if (loading) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "60vh" }}>
        <RefreshCw size={20} className="animate-spin" style={{ color: "#9CA3AF" }} />
        <span style={{ marginLeft: 10, fontSize: 13, color: "#9CA3AF", fontFamily: "Inter, sans-serif" }}>Chargement...</span>
      </div>
    );
  }

  const hasAlerts = alerts.oneshotOverdue.length > 0 || alerts.maintenanceOverdue.length > 0 || alerts.pendingValidation.length > 0;

  return (
    <div data-testid="superadmin-dashboard" style={{ fontFamily: "Inter, sans-serif" }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 28 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: COLORS.text, margin: 0, letterSpacing: -0.3 }}>Vue globale</h1>
          <p style={{ fontSize: 13, color: COLORS.textSecondary, marginTop: 4, textTransform: "capitalize" }}>{todayFormatted}</p>
        </div>
        <button
          data-testid="refresh-btn"
          onClick={loadData}
          disabled={isRefreshing}
          style={{
            display: "flex", alignItems: "center", gap: 6,
            padding: "8px 16px", borderRadius: 8,
            background: "#fff", border: "1px solid #E5E7EB",
            color: COLORS.text, fontSize: 13, fontWeight: 500, cursor: "pointer",
            fontFamily: "Inter, sans-serif",
          }}
        >
          <RefreshCw size={14} className={isRefreshing ? "animate-spin" : ""} /> Actualiser
        </button>
      </div>

      {/* KPI Cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginBottom: 24 }}>
        {[
          {
            icon: Store, iconBg: "#FEF3C7", iconColor: "#F59E0B",
            title: "Boutiques actives", value: kpis.activeBoutiques,
            delta: `+${kpis.newThisMonth} ce mois`, deltaUp: true,
          },
          {
            icon: Wallet, iconBg: "#D1FAE5", iconColor: "#10B981",
            title: "MRR", value: `${kpis.mrr.toLocaleString("fr-FR")} $`,
            delta: "+12.8%", deltaUp: true,
          },
          {
            icon: Users, iconBg: "#EDE9FE", iconColor: "#8B5CF6",
            title: "Total utilisateurs", value: kpis.totalUsers,
            subtitle: `sur ${kpis.totalBoutiques} boutiques`,
          },
          {
            icon: AlertTriangle, iconBg: "#FEE2E2", iconColor: "#EF4444",
            title: "Boutiques inactives", value: kpis.inactiveBoutiques,
            subtitle: "Sans connexion 30j",
            link: true,
          },
        ].map((card, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.06 }}
            onClick={card.link ? () => navigate("/super-admin/boutiques?filter=inactive") : undefined}
            data-testid={`kpi-card-${i}`}
            style={{
              background: "#fff", border: "1px solid #F0F0F0", borderRadius: 12,
              padding: 20, cursor: card.link ? "pointer" : "default",
              transition: "box-shadow 150ms",
            }}
            onMouseEnter={e => { e.currentTarget.style.boxShadow = "0 2px 12px rgba(0,0,0,0.06)"; }}
            onMouseLeave={e => { e.currentTarget.style.boxShadow = "none"; }}
          >
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 14 }}>
              <div style={{
                width: 40, height: 40, borderRadius: 10,
                background: card.iconBg,
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <card.icon size={20} style={{ color: card.iconColor }} />
              </div>
              {card.delta && (
                <span style={{
                  display: "inline-flex", alignItems: "center", gap: 3,
                  padding: "3px 8px", borderRadius: 20,
                  background: card.deltaUp ? "#ECFDF5" : "#FEF2F2",
                  color: card.deltaUp ? "#059669" : "#DC2626",
                  fontSize: 11, fontWeight: 600,
                }}>
                  {card.deltaUp ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                  {card.delta}
                </span>
              )}
            </div>
            <div style={{ fontSize: 11, color: COLORS.textSecondary, fontWeight: 500, marginBottom: 4 }}>{card.title}</div>
            <div style={{ fontSize: 26, fontWeight: 700, color: COLORS.text, letterSpacing: -0.5 }}>{card.value}</div>
            {card.subtitle && <div style={{ fontSize: 11, color: COLORS.textSecondary, marginTop: 2 }}>{card.subtitle}</div>}
            {card.link && (
              <div style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 8, fontSize: 12, color: COLORS.accent, fontWeight: 600, cursor: "pointer" }}>
                Voir détails <ArrowRight size={12} />
              </div>
            )}
          </motion.div>
        ))}
      </div>

      {/* Chart + Plan Breakdown row */}
      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 16, marginBottom: 24 }}>
        {/* Growth Chart */}
        <div data-testid="growth-chart" style={{ background: "#fff", border: "1px solid #F0F0F0", borderRadius: 12, padding: 24 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: COLORS.text }}>Évolution</div>
              <div style={{ fontSize: 12, color: COLORS.textSecondary, marginTop: 2 }}>
                {chartView === "mrr" ? "Revenus mensuels récurrents" : "Nouvelles inscriptions"}
              </div>
            </div>
            <div style={{ display: "flex", background: "#F3F4F6", borderRadius: 8, overflow: "hidden" }}>
              {["mrr", "newBoutiques"].map(v => (
                <button key={v} onClick={() => setChartView(v)} style={{
                  padding: "6px 14px", fontSize: 12, fontWeight: 600, border: "none", cursor: "pointer",
                  fontFamily: "Inter, sans-serif",
                  background: chartView === v ? COLORS.accent : "transparent",
                  color: chartView === v ? "#fff" : COLORS.textSecondary,
                  transition: "all 150ms",
                }}>
                  {v === "mrr" ? "MRR" : "Boutiques"}
                </button>
              ))}
            </div>
          </div>
          <div style={{ height: 240 }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.mrrHistory}>
                <defs>
                  <linearGradient id="gradOrange" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#FF4500" stopOpacity={0.08} />
                    <stop offset="95%" stopColor="#FF4500" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} />
                <XAxis dataKey="month" stroke="#D1D5DB" tick={{ fill: "#9CA3AF", fontSize: 11, fontFamily: "Inter" }} axisLine={false} tickLine={false} />
                <YAxis stroke="#D1D5DB" tick={{ fill: "#9CA3AF", fontSize: 11, fontFamily: "Inter" }} axisLine={false} tickLine={false}
                  tickFormatter={v => chartView === "mrr" ? `${v}$` : v} />
                <Tooltip
                  contentStyle={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 8, fontFamily: "Inter", fontSize: 12 }}
                  formatter={v => [chartView === "mrr" ? `${v} $` : v, chartView === "mrr" ? "MRR" : "Boutiques"]}
                />
                <Area type="monotone" dataKey={chartView === "mrr" ? "mrr" : "newBoutiques"}
                  stroke="#FF4500" strokeWidth={2.5} fill="url(#gradOrange)" dot={false} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Plan Breakdown */}
        <div data-testid="plan-breakdown" style={{ background: "#fff", border: "1px solid #F0F0F0", borderRadius: 12, padding: 24, display: "flex", flexDirection: "column" }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: COLORS.text, marginBottom: 20 }}>Répartition par plan</div>
          <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 16 }}>
            {planDistribution.map(p => {
              const barWidth = Math.max(p.pct, 4);
              return (
                <div key={p.key}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <div style={{ width: 8, height: 8, borderRadius: "50%", background: p.color, flexShrink: 0 }} />
                      <span style={{ fontSize: 13, fontWeight: 500, color: COLORS.text }}>{p.name}</span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span style={{ fontSize: 14, fontWeight: 700, color: COLORS.text }}>{p.value}</span>
                      <span style={{ fontSize: 11, color: COLORS.textSecondary }}>{p.pct}%</span>
                    </div>
                  </div>
                  <div style={{ height: 6, borderRadius: 3, background: "#F3F4F6", overflow: "hidden" }}>
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${barWidth}%` }}
                      transition={{ duration: 0.8, ease: "easeOut" }}
                      style={{ height: "100%", borderRadius: 3, background: p.color }}
                    />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Mini stat tiles */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 20, paddingTop: 16, borderTop: "1px solid #F3F4F6" }}>
            <div style={{ background: "#F9FAFB", borderRadius: 10, padding: "12px 14px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                <CreditCard size={13} style={{ color: COLORS.accent }} />
                <span style={{ fontSize: 10, fontWeight: 600, color: COLORS.textSecondary, textTransform: "uppercase" }}>Paiements</span>
              </div>
              <div style={{ fontSize: 18, fontWeight: 700, color: COLORS.text }}>{paymentStats.paidCount}</div>
              <div style={{ fontSize: 10, color: COLORS.textSecondary }}>reçus</div>
            </div>
            <div style={{ background: "#F9FAFB", borderRadius: 10, padding: "12px 14px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                <Percent size={13} style={{ color: "#8B5CF6" }} />
                <span style={{ fontSize: 10, fontWeight: 600, color: COLORS.textSecondary, textTransform: "uppercase" }}>Conversion</span>
              </div>
              <div style={{ fontSize: 18, fontWeight: 700, color: COLORS.text }}>{paymentStats.conversionRate}%</div>
              <div style={{ fontSize: 10, color: COLORS.textSecondary }}>taux</div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Signups + Alerts row */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        {/* Recent Signups */}
        <div data-testid="recent-signups" style={{ background: "#fff", border: "1px solid #F0F0F0", borderRadius: 12, padding: 24 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: COLORS.text }}>Dernières inscriptions</div>
            <button onClick={() => navigate("/super-admin/boutiques")}
              style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12, fontWeight: 600, color: COLORS.accent, background: "none", border: "none", cursor: "pointer", fontFamily: "Inter" }}>
              Voir tout <ArrowRight size={12} />
            </button>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {lastRegistrations.map((b, i) => (
              <div key={b.id} style={{
                display: "flex", alignItems: "center", gap: 12,
                padding: "10px 12px", borderRadius: 10,
                transition: "background 120ms",
              }}
              onMouseEnter={e => { e.currentTarget.style.background = "#F9FAFB"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
              >
                <div style={{
                  width: 38, height: 38, borderRadius: 10, flexShrink: 0,
                  background: AVATAR_COLORS[i % AVATAR_COLORS.length],
                  display: "flex", alignItems: "center", justifyContent: "center",
                  color: "#fff", fontSize: 13, fontWeight: 700, fontFamily: "Inter",
                }}>
                  {getInitials(b.name)}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{b.name}</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 2 }}>
                    <span style={{
                      padding: "1px 7px", borderRadius: 4, fontSize: 10, fontWeight: 600,
                      background: `${PLANS[b.plan]?.color}15`, color: PLANS[b.plan]?.color,
                    }}>{PLANS[b.plan]?.name}</span>
                    <span style={{ fontSize: 11, color: COLORS.textSecondary }}>
                      {format(new Date(b.createdAt), "d MMM yyyy", { locale: fr })}
                    </span>
                  </div>
                </div>
                <div style={{
                  width: 8, height: 8, borderRadius: "50%", flexShrink: 0,
                  background: b.status === "active" ? "#22C55E" : "#EF4444",
                }} title={b.status === "active" ? "Active" : "Inactive"} />
              </div>
            ))}
          </div>
        </div>

        {/* Alerts */}
        <div data-testid="alerts-section" style={{ background: "#fff", border: "1px solid #F0F0F0", borderRadius: 12, padding: 24 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: COLORS.text, marginBottom: 16 }}>Alertes</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {alerts.oneshotOverdue.length > 0 && (
              <button
                data-testid="alert-oneshot-overdue"
                onClick={() => navigate("/super-admin/payments")}
                style={{
                  width: "100%", display: "flex", alignItems: "center", gap: 12,
                  padding: "14px 16px", borderRadius: 12, border: "none", cursor: "pointer",
                  background: "#FFF7ED", textAlign: "left", fontFamily: "Inter",
                }}
              >
                <div style={{ width: 36, height: 36, borderRadius: 10, background: "#FFEDD5", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <DollarSign size={16} style={{ color: "#EA580C" }} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#9A3412" }}>
                    {alerts.oneshotOverdue.length} paiement{alerts.oneshotOverdue.length > 1 ? "s" : ""} non reçu{alerts.oneshotOverdue.length > 1 ? "s" : ""}
                  </div>
                  <div style={{ fontSize: 11, color: "#C2410C", marginTop: 2 }}>{truncateNames(alerts.oneshotOverdue)}</div>
                </div>
                <ArrowRight size={16} style={{ color: "#EA580C", flexShrink: 0 }} />
              </button>
            )}

            {alerts.maintenanceOverdue.length > 0 && (
              <button
                data-testid="alert-maintenance-overdue"
                onClick={() => navigate("/super-admin/payments")}
                style={{
                  width: "100%", display: "flex", alignItems: "center", gap: 12,
                  padding: "14px 16px", borderRadius: 12, border: "none", cursor: "pointer",
                  background: "#FEF2F2", textAlign: "left", fontFamily: "Inter",
                }}
              >
                <div style={{ width: 36, height: 36, borderRadius: 10, background: "#FEE2E2", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <Clock size={16} style={{ color: "#DC2626" }} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#991B1B" }}>
                    {alerts.maintenanceOverdue.length} maintenance{alerts.maintenanceOverdue.length > 1 ? "s" : ""} impayée{alerts.maintenanceOverdue.length > 1 ? "s" : ""}
                  </div>
                  <div style={{ fontSize: 11, color: "#B91C1C", marginTop: 2 }}>{truncateNames(alerts.maintenanceOverdue)}</div>
                </div>
                <ArrowRight size={16} style={{ color: "#DC2626", flexShrink: 0 }} />
              </button>
            )}

            {alerts.pendingValidation.length > 0 && (
              <button
                data-testid="alert-pending-validation"
                onClick={() => navigate("/super-admin/boutiques?filter=pending_validation")}
                style={{
                  width: "100%", display: "flex", alignItems: "center", gap: 12,
                  padding: "14px 16px", borderRadius: 12, border: "none", cursor: "pointer",
                  background: "#EFF6FF", textAlign: "left", fontFamily: "Inter",
                }}
              >
                <div style={{ width: 36, height: 36, borderRadius: 10, background: "#DBEAFE", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <ShieldX size={16} style={{ color: "#2563EB" }} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#1E3A8A" }}>
                    {alerts.pendingValidation.length} en attente de validation
                  </div>
                  <div style={{ fontSize: 11, color: "#1D4ED8", marginTop: 2 }}>{truncateNames(alerts.pendingValidation)}</div>
                </div>
                <ArrowRight size={16} style={{ color: "#2563EB", flexShrink: 0 }} />
              </button>
            )}

            {!hasAlerts && (
              <div style={{ textAlign: "center", padding: "32px 0", color: COLORS.textSecondary, fontSize: 13 }}>
                Aucune alerte active
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
